import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, CheckCircle2, Lock } from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import { PASSWORD_CRITERIA, getPasswordScore, isPasswordLongEnough } from "@/lib/passwordStrength";

const SetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      console.log("[SetPassword] Starting session check...");
      console.log("[SetPassword] Current URL:", window.location.href);
      console.log("[SetPassword] Hash:", window.location.hash);

      // 1. D'abord, vérifier s'il y a une erreur dans le hash URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorParam = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');
      
      if (errorParam) {
        console.error("[SetPassword] Error in URL hash:", errorParam, errorDescription);
        // Nettoyer le hash après avoir lu l'erreur
        window.history.replaceState(null, '', window.location.pathname);
        setIsValidSession(false);
        return;
      }

      // 2. Vérifier si une session existe déjà (tokens peut-être déjà consommés par AuthContext)
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("[SetPassword] Session error:", error);
        setIsValidSession(false);
        return;
      }

      if (session?.user) {
        console.log("[SetPassword] Valid session found for user:", session.user.email);
        // Nettoyer le hash si présent
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        setIsValidSession(true);
        return;
      }

      // 3. Pas de session existante, essayer de récupérer les tokens du hash
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');
      
      console.log("[SetPassword] Hash params - type:", type, "has access_token:", !!accessToken);

      if (accessToken && type === 'recovery') {
        try {
          console.log("[SetPassword] Attempting to set session from recovery tokens...");
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          // Nettoyer le hash après traitement
          window.history.replaceState(null, '', window.location.pathname);

          if (setSessionError) {
            console.error("[SetPassword] Set session error:", setSessionError);
            setIsValidSession(false);
          } else {
            console.log("[SetPassword] Session set successfully from recovery tokens");
            setIsValidSession(true);
          }
        } catch (e) {
          console.error("[SetPassword] Exception setting session:", e);
          window.history.replaceState(null, '', window.location.pathname);
          setIsValidSession(false);
        }
      } else {
        console.log("[SetPassword] No valid recovery tokens found, no existing session");
        // Nettoyer le hash si présent
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        setIsValidSession(false);
      }
    };

    checkSession();
  }, []);

  // Critères de force du mot de passe (règle partagée passwordStrength)
  const passwordScore = getPasswordScore(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordLongEnough(password)) {
      toast({
        title: "Mot de passe trop court",
        description: "Le mot de passe doit contenir au moins 8 caractères.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Mots de passe différents",
        description: "Les deux mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      toast({
        title: "Mot de passe configuré",
        description: "Votre compte est maintenant actif. Redirection...",
      });

      // Nettoyer le flag de recovery
      sessionStorage.removeItem('password_recovery_pending');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);

    } catch (error: any) {
      console.error("Error updating password:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de configurer le mot de passe.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidSession === null) {
    return <PiktableLoader message="Vérification..." />;
  }

  if (isValidSession === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Lien invalide ou expiré</CardTitle>
            <CardDescription>
              Ce lien de configuration de mot de passe n'est plus valide.
              Veuillez contacter votre administrateur pour recevoir un nouveau lien.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate("/login")}
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold">Compte activé !</h2>
              <p className="text-muted-foreground">
                Votre mot de passe a été configuré avec succès.
              </p>
              <Button className="w-full" onClick={() => navigate("/login")}>
                Se connecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Configurez votre mot de passe</CardTitle>
          <CardDescription>
            Créez un mot de passe pour activer votre compte Piktable
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Indicateur de force */}
              {password.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordScore >= i
                            ? passwordScore === 3
                              ? "bg-green-500"
                              : passwordScore === 2
                              ? "bg-yellow-500"
                              : "bg-red-500"
                            : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {PASSWORD_CRITERIA.map((criterion) => (
                      <li
                        key={criterion.key}
                        className={criterion.test(password) ? "text-green-600" : "text-muted-foreground"}
                      >
                        {criterion.test(password) ? "✓" : "○"} {criterion.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className="text-xs text-destructive">Les mots de passe ne correspondent pas.</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Configuration..." : "Activer mon compte"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
